// Lightweight app notifications rendered as a fixed overlay panel that slides up
// from the bottom-left of the viewport (see NotificationBanner). Notifications
// stay visible until explicitly dismissed. The pub/sub mechanism is intentionally
// simple so any module can report success / error / warning / info without prop-drilling.

export type NotificationType = "warning" | "error" | "success" | "info";

export interface AppNotification {
  id: string;
  type: NotificationType;
  message: string;
  /** Optional action button label + handler. */
  action?: { label: string; onClick: () => void };
  /** Auto-dismiss after this many ms. Omit for persistent notifications. */
  autoDismissMs?: number;
}

type UpdateFn = (notifications: AppNotification[]) => void;

let notifications: AppNotification[] = [];
const listeners = new Set<UpdateFn>();
let nextId = 1;

function broadcast(): void {
  const snapshot = [...notifications];
  listeners.forEach((cb) => cb(snapshot));
}

export const notificationService = {
  show(notification: Omit<AppNotification, "id">): string {
    const id = String(nextId++);
    const entry: AppNotification = { ...notification, id };
    notifications = [...notifications, entry];
    broadcast();

    if (notification.autoDismissMs) {
      setTimeout(() => notificationService.dismiss(id), notification.autoDismissMs);
    }
    return id;
  },

  update(id: string, patch: Partial<Omit<AppNotification, "id">>): void {
    notifications = notifications.map((n) => (n.id === id ? { ...n, ...patch } : n));
    broadcast();
  },

  dismiss(id: string): void {
    notifications = notifications.filter((n) => n.id !== id);
    broadcast();
  },

  getAll(): AppNotification[] {
    return notifications;
  },

  onUpdate(cb: UpdateFn): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
};
